/** @format */

import { type ChangeEvent } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDropZone from '../FileDropZone';
import { CloseIcon, DeleteIcon, EditIcon, ImageEditorIcon, ThreedotsIcon } from 'src/components/icons';
import type { ContentEditorPart, ContentPartUploadFn, PatchContentPartFn } from 'src/types';

interface ContentEditorBlockBodyImageTextProps {
  part: ContentEditorPart;
  onPatch: PatchContentPartFn;
  onFile?: ContentPartUploadFn;
  headlineError?: string;
  paragraphError?: string;
}

export default function ContentEditorBlockBodyImageText({
  part,
  onPatch,
  onFile = undefined,
  headlineError = '',
  paragraphError = ''
}: ContentEditorBlockBodyImageTextProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imageTextPart = part.type === 'imageText' ? part : null;
  const title = typeof imageTextPart?.title === 'string' ? imageTextPart.title : '';
  const text = typeof imageTextPart?.text === 'string' ? imageTextPart.text : '';
  const url =
    imageTextPart?.image && typeof imageTextPart.image.url === 'string' ? imageTextPart.image.url : '';
  const showPreview = Boolean(url);
  const uploadFile = Array.isArray(imageTextPart?._file) ? imageTextPart._file[0] : imageTextPart?._file;
  const displayName = uploadFile?.name || (imageTextPart?.image?.title as string | undefined) || '';
  const inputId = `content-editor-file-${part.id}`;

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleChangeImage = () => {
    setMenuOpen(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDeleteImage = () => {
    setMenuOpen(false);
    if (typeof onFile === 'function') {
      onFile(null);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file && typeof onFile === 'function') {
      onFile(file);
    }
    if (event?.target) {
      event.target.value = '';
    }
  };

  return (
    <div className="content-editor__image-text-layout">
      <div className="content-editor__image-text-media">
        {showPreview ? (
          <div className="content-editor__image-preview-wrap">
            <div className="content-editor__remote-image-preview content-editor__remote-image-preview--image-text">
              <img
                src={url}
                alt={typeof imageTextPart?.image?.alt === 'string' ? imageTextPart.image.alt : ''}
                className="content-editor__remote-image-preview-img"
              />
            </div>
            <div className="content-editor__image-actions" ref={menuRef}>
              <button
                type="button"
                className="content-editor__image-actions-toggle"
                onClick={() => setMenuOpen(open => !open)}
                aria-label={t('contentEditor.imageActions')}
                aria-expanded={menuOpen}>
                <ThreedotsIcon />
              </button>
              {menuOpen ? (
                <div className="content-editor__image-actions-menu" role="menu">
                  <button
                    type="button"
                    className="content-editor__image-actions-item"
                    role="menuitem"
                    onClick={handleChangeImage}>
                    <EditIcon width={20} height={20} />
                    <span>{t('common.button.change')}</span>
                  </button>
                  <button
                    type="button"
                    className="content-editor__image-actions-item"
                    role="menuitem"
                    onClick={handleDeleteImage}>
                    <DeleteIcon width={20} height={20} />
                    <span>{t('common.button.delete')}</span>
                  </button>
                </div>
              ) : null}
              <input
                id={inputId}
                ref={inputRef}
                type="file"
                accept="image/*"
                className="content-editor__dropzone-input"
                onChange={handleFileChange}
              />
            </div>
          </div>
        ) : null}
        {!showPreview ? (
          <FileDropZone
            inputId={inputId}
            accept="image/*"
            className="content-editor__dropzone--image-text"
            leading={<ImageEditorIcon />}
            label={t('contentEditor.imageTextDropLine1')}
            labelSecondary={t('contentEditor.imageTextDropLine2')}
            fileName={displayName}
            onFile={onFile}
          />
        ) : null}
      </div>
      <div className="content-editor__image-text-fields">
        <div className="content-editor__image-text-stack">
          <label className="content-editor__image-text-field-label" htmlFor={`content-editor-title-${part.id}`}>
            {t('contentEditor.headline')}
          </label>
          <TextField
            id={`content-editor-title-${part.id}`}
            fullWidth
            variant="outlined"
            margin="none"
            value={title}
            helperText={headlineError || undefined}
            FormHelperTextProps={
              headlineError ? { className: 'content-editor__field-error', role: 'alert' } : undefined
            }
            onChange={e => onPatch(part.id, { title: e.target.value })}
            InputProps={{
              endAdornment: title ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('contentEditor.ariaClearHeadline')}
                    onClick={() => onPatch(part.id, { title: '' })}>
                    <CloseIcon width={14} height={14} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        </div>
        <div className="content-editor__image-text-stack">
          <label className="content-editor__image-text-field-label" htmlFor={`content-editor-text-${part.id}`}>
            {t('contentEditor.paragraph')}
          </label>
          <div className="content-editor__image-text-field-wrap content-editor__image-text-field-wrap--multiline">
            {text ? (
              <IconButton
                type="button"
                size="small"
                className="content-editor__image-text-field-clear"
                aria-label={t('contentEditor.ariaClearParagraph')}
                onClick={() => onPatch(part.id, { text: '' })}>
                <CloseIcon width={14} height={14} />
              </IconButton>
            ) : null}
            <TextField
              id={`content-editor-text-${part.id}`}
              fullWidth
              variant="outlined"
              margin="none"
              multiline
              minRows={5}
              value={text}
              helperText={paragraphError || undefined}
              FormHelperTextProps={
                paragraphError ? { className: 'content-editor__field-error', role: 'alert' } : undefined
              }
              onChange={e => onPatch(part.id, { text: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
